import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Save, Eye } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { OnboardingProgress } from '../../components/business/onboarding/OnboardingProgress';
import { StepBusinessIdentity } from '../../components/business/onboarding/StepBusinessIdentity';
import { StepAddress } from '../../components/business/onboarding/StepAddress';
import { StepHours } from '../../components/business/onboarding/StepHours';
import { StepFulfillment } from '../../components/business/onboarding/StepFulfillment';
import { StepInventory } from '../../components/business/onboarding/StepInventory';
import { StepLegal } from '../../components/business/onboarding/StepLegal';
import { StepSocial } from '../../components/business/onboarding/StepSocial';
import { StepReview } from '../../components/business/onboarding/StepReview';
import { useBoutiqueOnboarding } from '../../hooks/useBoutiqueOnboarding';
import { useAuth } from '../../hooks/useAuth';
import { ONBOARDING_STEPS } from '../../types/business';
import { FASH_LOGO } from '../../constants/brand';

export function BoutiqueOnboardingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    formData,
    currentStep,
    saving,
    loading,
    error,
    stepCompletion,
    updateField,
    nextStep,
    prevStep,
    goToStep,
    activateBoutique,
    setError,
  } = useBoutiqueOnboarding();

  const handleActivate = async () => {
    const success = await activateBoutique();
    if (success) {
      navigate('/business');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-pulse">
            <img src={FASH_LOGO} alt="Fash" className="h-16 w-16 object-contain" />
          </div>
          <p className="mt-4 text-body font-sans">Loading your progress...</p>
        </div>
      </div>
    );
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepBusinessIdentity
            formData={formData}
            updateField={updateField}
            error={error}
          />
        );
      case 2:
        return (
          <StepAddress
            formData={formData}
            updateField={updateField}
            error={error}
          />
        );
      case 3:
        return (
          <StepHours
            formData={formData}
            updateField={updateField}
            error={error}
          />
        );
      case 4:
        return (
          <StepFulfillment
            formData={formData}
            updateField={updateField}
            error={error}
          />
        );
      case 5:
        return (
          <StepInventory
            formData={formData}
            updateField={updateField}
            error={error}
          />
        );
      case 6:
        return (
          <StepLegal
            formData={formData}
            updateField={updateField}
            error={error}
          />
        );
      case 7:
        return (
          <StepSocial
            formData={formData}
            updateField={updateField}
            error={error}
          />
        );
      case 8:
        return (
          <StepReview
            formData={formData}
            stepCompletion={stepCompletion}
            onGoToStep={goToStep}
            onActivate={handleActivate}
            saving={saving}
            error={error}
          />
        );
      default:
        return null;
    }
  };

  const isLastStep = currentStep === ONBOARDING_STEPS.length;
  const isFirstStep = currentStep === 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50">
      {/* Top Bar */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={FASH_LOGO} alt="Fash" className="h-8 w-8 object-contain" />
            <span className="text-sm font-medium text-heading hidden sm:block">
              Create Your Boutique
            </span>
          </Link>

          <div className="flex items-center gap-3">
            {saving && (
              <span className="text-xs text-body flex items-center gap-1.5">
                <Save className="h-3.5 w-3.5 animate-pulse" />
                Saving...
              </span>
            )}
            {currentStep >= 2 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(`/boutique/${user?.uid}`, '_blank')}
                className="text-xs"
              >
                <Eye className="h-3.5 w-3.5 mr-1" />
                Preview
              </Button>
            )}
            <span className="text-xs text-body">
              Step {currentStep} of {ONBOARDING_STEPS.length}
            </span>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Progress Stepper */}
        <div className="mb-8">
          <OnboardingProgress
            currentStep={currentStep}
            stepCompletion={stepCompletion}
            onStepClick={goToStep}
          />
        </div>

        {/* Step Content */}
        <Card className="shadow-xl border-0 mb-6">
          <CardContent className="p-6 sm:p-8">
            {/* Error Banner */}
            {error && currentStep !== 8 && (
              <div className="bg-error/10 text-error text-sm p-3 rounded-lg mb-6 flex items-start gap-2">
                <span className="flex-shrink-0 mt-0.5">!</span>
                <span>{error}</span>
              </div>
            )}

            {renderStep()}
          </CardContent>
        </Card>

        {/* Navigation Buttons (hidden on review step since it has its own button) */}
        {!isLastStep && (
          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={prevStep}
              disabled={isFirstStep}
              className={isFirstStep ? 'invisible' : ''}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>

            <Button
              type="button"
              onClick={nextStep}
              loading={saving}
              size="lg"
            >
              Continue
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Back button on review step */}
        {isLastStep && (
          <div className="flex justify-start">
            <Button
              type="button"
              variant="outline"
              onClick={prevStep}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
